import { FC, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { important } from 'common-types';
import { useInfinityScroll } from 'react-utils';
import { navigateToPipelineIssues } from '../../common/routing/app-navigator';
import OxDataGrid from '../../elements/OxDataGrid/OxDataGrid';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import { loadPipelineSummary } from '../actions/pipeline-summary-actions';
import config from '../config/pipeline-summary-config.json';
import { usePipelineSummaryHeader } from '../hooks/use-pipeline-summary-header';
import { PipelineSummary } from '../pipeline-summary-types';

const PipelineSummaryTable: FC<PipelineSummaryTableProps> = ({
  pipelineSummaries,
  loading,
}) => {
  const { classes } = useStyles();
  const columns = usePipelineSummaryHeader(classes);
  const { onScroll } = useInfinityScroll({
    load: loadPipelineSummary,
    threshold: 0.9,
  });

  const onRowClick = useCallback((row: PipelineSummary) => {
    if (!+row.totalIssues) {
      openSnackbar('The pipeline scan has no issues to show!', {
        variant: 'success',
      });
    } else {
      navigateToPipelineIssues({ jobNumber: [row.jobId] });
    }
  }, []);

  return (
    <div className={classes.tableContainer}>
      <OxDataGrid
        rowHeight={config.table.rowHeight}
        onScroll={onScroll}
        columns={columns}
        rows={pipelineSummaries}
        loading={loading}
        onRowClick={onRowClick}
      />
    </div>
  );
};

const useStyles = makeStyles()({
  tableContainer: {
    borderRadius: 10,
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  textCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    flexDirection: 'row',
  },
  flexCenter: {
    justifyContent: 'center',
  },
  headerCell: {
    textAlign: 'center',
    '& .rdg-header-sort-name': {
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      lineHeight: 1,
      alignSelf: 'center',
      fontSize: 10,
    },
  },
  noPadding: {
    paddingInline: important('0'),
  },
});

export interface PipelineSummaryTableProps {
  pipelineSummaries: PipelineSummary[];
  loading: boolean;
}

export default PipelineSummaryTable;
